import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { listProperties } from '../api';
import ListingImage from '../components/ListingImage';
import MobileFilterPanel from '../components/MobileFilterPanel';
import FilterFields from '../components/FilterFields';
import useBrowseFilters from '../hooks/useBrowseFilters';
import { PROPERTY_CATEGORIES, PROPERTY_AMENITIES } from '../listingOptions';

// fix_issue3.md #2 - sort options: "Sort by High to Low Price" and "Best to Low ratings" are the
// two the requirement calls out; recommended + low-to-high price round it out.
const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'rating', label: 'Rating: Best to Low' }
];

// Structured browse filters, matched 1:1 with the fields on the "list a property" form
// (PropertyForm.js). Bedrooms / bathrooms are "N or more" minimums; amenity is array-contains.
const asOpts = (arr) => arr.map((o) => ({ value: o, label: o }));
const FILTERS = [
  { key: 'category', label: 'Type', options: asOpts(PROPERTY_CATEGORIES) },
  { key: 'bedrooms', label: 'Bedrooms', options: [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n}+ BHK` })) },
  { key: 'bathrooms', label: 'Bathrooms', options: [1, 2, 3, 4].map((n) => ({ value: String(n), label: `${n}+` })) },
  { key: 'amenity', label: 'Amenity', options: asOpts(PROPERTY_AMENITIES) }
];
const FILTER_KEYS = FILTERS.map((f) => f.key);

// Real estate - the budget slider ceiling is itself a control, because a property listing can be
// anything from a house to rent to a villa to buy. Starts on the house-rent range.
const PRICE_PROPS = {
  ceilingOptions: [
    { value: 100000, label: '₹1 Lakh — house rent' },
    { value: 200000, label: '₹2 Lakh/day — villa rent' },
    { value: 20000000, label: '₹2 Crore — house sale' },
    { value: 50000000, label: '₹5 Crore — villa sale' }
  ],
  defaultCeiling: 100000,
  allowCustomCeiling: true
};

// homepage/user_experience.md #1 - a deep initial page + keep-loading-more.
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const SCROLL_KEY = 'rv:listings';

// #1 - a compact "Trending now" rail, shown only on the pristine browse view. Deliberately NOT
// <Card>/.card markup so the results grid stays the first .card on the page.
function TrendingStrip({ items, browsePath }) {
  if (!items.length) return null;
  return (
    <section aria-label="Trending now" className="mb-2">
      <h5 className="mb-2">Trending now</h5>
      <div className="rv-trending">
        {items.map((item) => (
          <Link key={item._id} to={`/items/${item._id}`} state={{ from: browsePath }} className="rv-trending-card">
            {item.images?.[0] && (
              <ListingImage
                src={item.images[0]}
                alt={item.name}
                sizes="(max-width: 576px) 46vw, 200px"
                style={{ width: '100%', height: 110, objectFit: 'cover' }}
              />
            )}
            <div className="p-2">
              <div className="small fw-semibold text-truncate">{item.name}</div>
              <div className="small text-muted text-truncate">{item.location}</div>
              <div className="small">₹{item.price}/{item.priceUnit}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function ItemList() {
  const browse = useBrowseFilters(FILTER_KEYS);
  const [searchInput, setSearchInput] = useState(browse.q);
  const location = useLocation();
  const [localities, setLocalities] = useState([]);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const isPristine = !browse.q && browse.activeCount === 0 && browse.sort === 'recommended';
  // #1 - the "Trending now" rail is just the top of the (recommended-sorted) pristine feed - no
  // extra request, and it never appears once a search / filter / sort is active.
  const trending = isPristine && items.length >= 12 ? items.slice(0, 10) : [];
  const restoredRef = useRef(false);

  useEffect(() => { setSearchInput(browse.q); }, [browse.q]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== browse.q) browse.setParam('q', searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setPage(1);
    listProperties({ ...browse.apiParams, limit: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setItems(res.data || []);
        setLocalities(res.localities || []);
        setPagination(res.pagination || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Listings load failed:', err.response?.status, err.response?.data || err.message);
        setError("We couldn't load listings right now. Please try again in a moment.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browse.sig]);

  useEffect(() => {
    if (loading || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      if (saved && saved.url === `${location.pathname}${location.search}`) {
        window.scrollTo(0, saved.y || 0);
      }
    } catch { /* ignore */ }
  }, [loading, location.pathname, location.search]);

  const rememberScroll = useCallback(() => {
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({
        url: `${location.pathname}${location.search}`,
        y: window.scrollY
      }));
    } catch { /* ignore */ }
  }, [location.pathname, location.search]);

  useEffect(() => rememberScroll, [rememberScroll]);

  const resultCount = pagination?.total ?? items.length;

  const loadMore = async () => {
    if (!pagination?.hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await listProperties({ ...browse.apiParams, page: nextPage, limit: PAGE_SIZE });
      setItems((prev) => [...prev, ...(res.data || [])]);
      setPagination(res.pagination || null);
      setPage(nextPage);
    } catch {
      setError('Could not load more listings right now.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div>
      <Form className="mb-3" onSubmit={(e) => e.preventDefault()} role="search">
        <Form.Control type="search" name="q" aria-label="Search properties" placeholder="Search by name, category, or location" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
      </Form>

      {/* Wide screens: filters inline. */}
      <div className="d-none d-lg-flex flex-wrap gap-2 align-items-end mb-4">
        <FilterFields localities={localities} browse={browse} filters={FILTERS} sortOptions={SORT_OPTIONS} priceProps={PRICE_PROPS} />
        {browse.activeCount > 0 && (
          <Button variant="link" size="sm" className="px-0 text-decoration-none" onClick={browse.clearAll}>
            Clear filters ({browse.activeCount})
          </Button>
        )}
      </div>

      {/* Narrow screens: a floating button opens the right-hand filter panel. */}
      <MobileFilterPanel
        activeCount={browse.activeCount}
        resultCount={loading ? undefined : resultCount}
        resultNoun="property"
        onClearAll={browse.clearAll}
      >
        <FilterFields localities={localities} browse={browse} filters={FILTERS} sortOptions={SORT_OPTIONS} priceProps={PRICE_PROPS} stacked />
      </MobileFilterPanel>

      {!loading && !error && isPristine && <TrendingStrip items={trending} browsePath={browse.browsePath} />}

      {loading && <Spinner animation="border" role="status" />}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && items.length === 0 && (
        <Alert variant="light">
          {browse.activeCount || browse.q ? 'No properties match these filters.' : 'No properties found.'}
        </Alert>
      )}

      {!loading && !error && items.length > 0 && (
        <h5 className="mb-3">{isPristine ? 'All listings' : `${resultCount} ${resultCount === 1 ? 'result' : 'results'}`}</h5>
      )}

      <Row xs={1} md={2} lg={3} className="g-4">
        {items.map((item, i) => (
          <Col key={item._id}>
            <Card className="h-100 shadow-sm">
              {item.images?.[0] && (
                <div className="position-relative">
                  <ListingImage
                    src={item.images[0]}
                    alt={item.name}
                    eager={i < 3}
                    sizes="(max-width: 768px) 100vw, (max-width: 992px) 50vw, 33vw"
                    className="card-img-top"
                    style={{ height: 180, objectFit: 'cover' }}
                  />
                  {item.images.length > 1 && (
                    <Badge bg="dark" className="position-absolute bottom-0 end-0 m-2 bg-opacity-75">
                      📷 {item.images.length}
                    </Badge>
                  )}
                </div>
              )}
              <Card.Body className="d-flex flex-column">
                <Card.Title>{item.name}</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                  {item.category} &middot; {item.location}
                </Card.Subtitle>
                <Card.Text className="flex-grow-1">{item.description}</Card.Text>
                <div className="mb-2">
                  <Badge bg="secondary" className="me-1">{item.attributes?.bedrooms} BHK</Badge>
                  <Badge bg="secondary" className="me-1">{item.attributes?.bathrooms} bath</Badge>
                  <Badge bg="secondary">{item.attributes?.areaSqft} sqft</Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <strong>₹{item.price}/{item.priceUnit}</strong>
                  <Button
                    as={Link}
                    to={`/items/${item._id}`}
                    state={{ from: browse.browsePath }}
                    onClick={rememberScroll}
                    variant="outline-primary"
                  >
                    View &amp; Request Visit
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {!loading && pagination?.hasMore && (
        <div className="text-center mt-4">
          <Button variant="outline-primary" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      )}
    </div>
  );
}
